/**
 * 전장을 끌어 경로를 그리는 손가락 하나. DOM도 픽시도 모른다 — 화면 좌표와 그 순간의
 * 화면→월드 변환, 시각만 받는다. 입력(`input.ts`)과 카메라(`renderer.ts`)가 함께 쓰고,
 * 시험이 브라우저 없이 돌린다.
 *
 * **손가락은 화면 좌표로 잰다.** 처음에는 월드 좌표로 쟀다. 그런데 끄는 동안 카메라가
 * 앞을 보려고 움직이므로, 손가락이 서 있어도 그 아래 월드 좌표는 계속 흘렀다. 흐른
 * 만큼 경로가 늘고, 늘어난 쪽으로 카메라가 또 움직였다 — 플레이한 사람이 "손가락을
 * 멈춰도 카메라가 가면서 경로 지정이 계속된다"고 했다. 이제 끌기 시작, 방향, 떨림은
 * 화면에서 재고, 월드 좌표는 지점을 찍을 때만 뽑는다. 손가락이 서면 지점도 안 찍히고,
 * 카메라도 선다(`lookTarget`).
 */
import { MAX_WAYPOINTS } from '../sim/commands.ts';

/** 끄는 동안 지점을 솎는 간격(월드 픽셀). 이보다 가까운 지점은 싣지 않는다. */
export const PATH_STEP_PX = 40;
/** 이만큼 움직여야 끌기다(화면 픽셀). 그 안이면 누른 것이다. */
export const DRAG_START_PX = 12;
/** 이보다 작은 움직임(화면 픽셀)은 손가락 떨림으로 보고 버린다. */
export const FINGER_JITTER_PX = 3;
/** 손가락이 이만큼(ms) 움직이지 않으면 끄는 중이라도 카메라를 세운다. */
export const LOOK_STILL_MS = 150;
/** 끄는 동안 카메라가 손가락 앞을 보는 거리. 화면 짧은 변에 대한 몫이다. */
export const LOOK_AHEAD = 0.2;

export interface PathPoint {
  x: number;
  y: number;
  /** 새 경로의 첫 지점이면 참. 이전 경로를 갈아 치운다(Rally). */
  start: boolean;
}

/** 끌고 있는 곳(월드 픽셀), 나아가는 방향(화면), 손가락이 마지막으로 움직인 시각. */
export interface DragLook {
  x: number;
  y: number;
  dirX: number;
  dirY: number;
  movedAt: number;
}

type ToWorld = (sx: number, sy: number) => { x: number; y: number };

export class PathDrag {
  /** 끌기로 넘어갔는가. 넘어가지 않고 떼면 누른 것이다(마우스면 휩쓸기). */
  dragging = false;
  /** 카메라가 볼 곳. 끄는 중이 아니면 null. */
  look: DragLook | null = null;
  private downSX = 0;
  private downSY = 0;
  private prevSX = 0;
  private prevSY = 0;
  private lastX = 0;
  private lastY = 0;
  private points = 0;

  down(sx: number, sy: number, toWorld: ToWorld): void {
    this.downSX = this.prevSX = sx;
    this.downSY = this.prevSY = sy;
    const p = toWorld(sx, sy);
    this.lastX = p.x;
    this.lastY = p.y;
    this.dragging = false;
    this.points = 0;
    this.look = null;
  }

  /** 손가락이 움직였다. 새로 찍힌 지점을 `out`에 싣는다. */
  move(sx: number, sy: number, toWorld: ToWorld, now: number, out: PathPoint[]): void {
    if (!this.dragging) {
      if (Math.hypot(sx - this.downSX, sy - this.downSY) < DRAG_START_PX) return;
      this.dragging = true;
      out.push({ x: Math.round(this.lastX), y: Math.round(this.lastY), start: true });
      this.points = 1;
    }
    // 떨림은 버린다. 손가락이 멈춘 채 1~2px 떨리는 것으로 카메라가 깨어나면 안 된다.
    const sdx = sx - this.prevSX;
    const sdy = sy - this.prevSY;
    const slen = Math.hypot(sdx, sdy);
    if (slen < FINGER_JITTER_PX) return;
    this.prevSX = sx;
    this.prevSY = sy;
    const p = toWorld(sx, sy);
    this.look = { x: p.x, y: p.y, dirX: sdx / slen, dirY: sdy / slen, movedAt: now };
    if (Math.hypot(p.x - this.lastX, p.y - this.lastY) < PATH_STEP_PX || this.points >= MAX_WAYPOINTS) return;
    this.lastX = p.x;
    this.lastY = p.y;
    out.push({ x: Math.round(p.x), y: Math.round(p.y), start: false });
    this.points++;
  }

  up(): void {
    this.dragging = false;
    this.look = null;
  }
}

/**
 * 끄는 동안 카메라가 볼 곳. 손가락 지점이 아니라 그 앞이다. `still`이면 카메라를
 * 그 자리에 둔다.
 *
 * 손가락을 화면 가운데 두면 나아가는 쪽이 반밖에 안 보인다. 화면이 작은 모바일에서
 * 긴 우회로를 그리려면 가는 쪽이 넓게 보여야 한다. 그래서 손가락에서 나아가는 방향으로
 * 화면 짧은 변의 `LOOK_AHEAD`만큼 앞을 가운데 둔다.
 *
 * **손가락이 서면 카메라도 선다.** 처음에는 30% 앞을 빠르게(프레임당 18%) 따라갔고,
 * 손가락이 서 있어도 계속 앞을 향했다. 플레이한 사람이 "카메라가 너무 빠르고, 손가락을
 * 멈춰도 카메라가 가면서 경로가 계속 그려진다"고 했다. 마지막으로 움직인 지
 * `LOOK_STILL_MS`가 지나면 선다. 느리게 따라가는 것은 렌더러의 몫이다(`LOOK_LERP`).
 */
export function lookTarget(
  look: DragLook,
  now: number,
  viewW: number,
  viewH: number,
): { x: number; y: number; still: boolean } {
  const ahead = Math.min(viewW, viewH) * LOOK_AHEAD;
  return {
    x: look.x + look.dirX * ahead,
    y: look.y + look.dirY * ahead,
    still: now - look.movedAt > LOOK_STILL_MS,
  };
}
